'use client';

import { useMemo, useState } from 'react';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import parse from 'html-react-parser';

import { Box, Collapse, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { LocationPackageInterface } from 'types';

import { Button, Divider } from 'ui';
import { SubPackageItem } from '../SubPackageItem';

interface PackagesItemProps {
  item: LocationPackageInterface;
  selectedPackages: number[];
  locationId: number;
}

const PackagesItem = ({
  item,
  selectedPackages,
  locationId
}: PackagesItemProps) => {
  const {
    colors: {
      palette: {
        shop: {
          packages: {
            stepPackages: { item: itemColors }
          }
        }
      }
    }
  } = useTheme();

  const hasSelectedSubpackages = useMemo(
    () =>
      item.pkg_list.some((subItem) => selectedPackages.includes(subItem.id)),
    [item, selectedPackages]
  );

  const [showSubpackages, setShowSubpackages] = useState(
    hasSelectedSubpackages
  );

  const isSubPackagesExist = item.pkg_list.length > 0;

  const toggleSubpackages = () => {
    setShowSubpackages((prevState) => !prevState);
  };

  return (
    <Stack
      w='100%'
      gap={0}
      pt={{ base: '20px', md: '30px' }}
      bg={showSubpackages ? itemColors.bgSelected : 'transparent'}
    >
      <Stack
        gap={{ base: '15px', md: '25px' }}
        px={{ base: '15px', md: '20px' }}
      >
        <Flex alignItems='center' justifyContent='space-between' gap='20px'>
          <Text
            fontSize='18px'
            fontWeight={{ base: 400, md: 600 }}
            lineHeight={1}
          >
            {parse(item.name ?? '')}
          </Text>

          <Box>
            <Button
              variant='brand'
              colorScheme='primaryLight'
              borderRadius='6px'
              onClick={toggleSubpackages}
              fontSize={{ base: '16px', lg: '18px' }}
              width={{ lg: '203px' }}
              height={{ lg: '50px' }}
              px={{ base: '12px', lg: '20px' }}
              py='12px'
              bg={
                showSubpackages
                  ? itemColors.buttonBgSelected
                  : itemColors.buttonBg
              }
              color={
                showSubpackages
                  ? itemColors.buttonTextSelected
                  : itemColors.buttonText
              }
              sx={{
                '& svg': {
                  fill: showSubpackages
                    ? itemColors.iconSelected
                    : itemColors.icon
                }
              }}
              rightIcon={
                <ChevronDown
                  width={14}
                  height={14}
                  fill='white'
                  style={{
                    flexShrink: 0,
                    margin: '0 0 1px 8px',
                    transition: 'transform 0.2s, fill 0.2s',
                    transform: showSubpackages ? 'scale(-1)' : 'scale(1)'
                  }}
                />
              }
              equalIconSpace={false}
            >
              View Packages
            </Button>
          </Box>
        </Flex>

        {item.dscr_line && (
          <Text fontSize='14px' lineHeight={1.6} color={itemColors.description}>
            {parse(item.dscr_line ?? '')}
          </Text>
        )}
      </Stack>

      {isSubPackagesExist && (
        <Collapse in={showSubpackages}>
          <Box>
            <Divider
              borderColor={itemColors.subPackageDivider}
              mt='20px'
              mb={{ base: '20px', md: '30px' }}
              opacity={0.1}
            />

            {item.pkg_list.map((subItem, index) => (
              <SubPackageItem
                key={subItem.id}
                item={subItem}
                isInsideMasterPackage
                isLastItem={item.pkg_list.length - 1 === index}
                locationId={locationId}
              />
            ))}
          </Box>
        </Collapse>
      )}

      <Divider
        borderColor={
          showSubpackages ? itemColors.dividerSelected : itemColors.divider
        }
        mt={{ base: '20px', md: '30px' }}
      />
    </Stack>
  );
};

export default PackagesItem;
